import { HttpErrorResponse }             from '@angular/common/http';
import { Injectable }                    from '@angular/core';
import {
	ActivatedRouteSnapshot,
	Router,
	RouterStateSnapshot
}                                        from '@angular/router';
import { ShowInvalidTenantModalService } from '@app/core/modals/show-invalid-tenant/show-invalid-tenant-modal.service';
import { AuthorizationService }          from '@app/core/services/authorization/authorization.service';
import { Tenant }                        from '@app/modules/admin-tenant/models/tenant.model';
import { RoleTypes }                     from '@app/shared/models/roles.model';
import { CurrentUserService }            from '@app/shared/services/current-user.service';
import { LoggerService }                 from '@app/shared/services/logger.service';
import { TenantService }                 from '@app/shared/services/tenant.service';
import { ToasterService }                from '@app/shared/services/toaster.service';
import { RequestCompleteEntityState }    from '@core/interfaces/api.interface';
import { TranslationService }            from '@core/services/translation.service';
import { KeycloakUtils }                 from '@utils/helpers';
import {
	KeycloakAuthGuard,
	KeycloakService
}                                        from 'keycloak-angular';
import {
	Observable,
	of
}                                        from 'rxjs';
import {
	catchError,
	map,
	take
}                                        from 'rxjs/operators';


@Injectable()
export class AuthGuard extends KeycloakAuthGuard {
	private _hasValidTenant: boolean;
	private _i18nAuthorizationBasePath = 'shared.authorization';
	private _isSupportAdmin: boolean;
	private _logoutInProcess: boolean;
	private _previousRoute: string = null;

	constructor(
		private readonly _authorizationService: AuthorizationService,
		private readonly _currentUserService: CurrentUserService,
		private readonly _logger: LoggerService,
		private readonly _showInvalidTenantModalService: ShowInvalidTenantModalService,
		private readonly _tenantService: TenantService,
		private readonly _toaster: ToasterService,
		private readonly _translationService: TranslationService,
		protected readonly keycloakService: KeycloakService,
		protected readonly router: Router
	) {
		super( router, keycloakService );
	}

	private hasValidTenant(): Observable<boolean> {
		return this._tenantService.currentUsersTenant()
		.pipe(
			map( ( tenantState: RequestCompleteEntityState<Tenant> ) => !!tenantState.entity ),
			catchError( ( error: HttpErrorResponse ) => of( false ) ),
			take( 1 )
		);
	}

	private logOut(): Promise<boolean> {
		if ( !this._logoutInProcess ) {
			this._logoutInProcess = true;
			this._previousRoute = null;
			this._showInvalidTenantModalService.showInvalidTenantModalComponent();
		}
		return Promise.resolve( true );
	}

	private checkAllowedRoute( state: RouterStateSnapshot ): Promise<boolean> {
		const isAllowed = this._authorizationService.hasPermissionForRoute( state.url );

		// not allowed route but comes from previous route
		if ( isAllowed === false && this._previousRoute ) {
			if ( state.url !== this._previousRoute ) {
				this.router.navigate( [ this._previousRoute ] )
				.then( () => {
					const toasterTitle = this._translationService.getI18nString(
						true,
						this._i18nAuthorizationBasePath,
						'routeAccessForbidden.title'
					);

					this._toaster.warning( '', toasterTitle );
				} )
				.catch( ( error ) => {
					this._toaster.error( 'AuthGuard:checkAllowedRoute from previous route->error', error );
				} );
			}
			return Promise.resolve( true );
		}

		// not allowed route but role has default route
		const defaultRoute = this._authorizationService.getDefaultRoute();
		if ( isAllowed === false && defaultRoute ) {
			this._previousRoute = defaultRoute;
			this.router.navigate( [ defaultRoute ] ).then( () => {
				const toasterTitle = this._translationService.getI18nString(
					true,
					this._i18nAuthorizationBasePath,
					'redirectToDefaultRoute.title'
				);

				const toasterMessage = this._translationService.getI18nString(
					true,
					this._i18nAuthorizationBasePath,
					'redirectToDefaultRoute.message'
				);

				this._toaster.warning( toasterMessage, toasterTitle );
			} )
			.catch( ( error ) => {
				this._toaster.error( 'AuthGuard:checkAllowedRoute has default route->error', error );
			} );

			return Promise.resolve( true );
		}

		// not allowed route
		if ( isAllowed === false ) {
			return this.logOut();
		}

		// allowed route
		if ( isAllowed ) {
			this._previousRoute = state.url;
			return Promise.resolve( true );
		}
	}

	isAccessAllowed( route: ActivatedRouteSnapshot, state: RouterStateSnapshot ): Promise<boolean> {

		if ( this.authenticated && !!KeycloakUtils.deeplinkSessionStorageGet() ) {
			const deeplinkUri: string = KeycloakUtils.deeplinkSessionStorageGet( { readAndDelete: true, decodeURIComponent: true } );
			const isAllowed = this._authorizationService.hasPermissionForRoute( `/${ deeplinkUri }` );

			const urlSegments: string[] = deeplinkUri?.length && isAllowed
																		? [ ...deeplinkUri?.split( '/' ) ]
																		: [ window.location.origin ];

			return this.router.navigate( urlSegments, { replaceUrl: true } );
		}

		// skip user validation check if already done once and continue with route access check
		if ( this._hasValidTenant || this._isSupportAdmin ) {
			return this.checkAllowedRoute( state );
		}

		// makes sure that validation is done only once and not repeated during running logout process
		if ( !this._logoutInProcess ) {
			// check for valid Tenant
			return this.hasValidTenant().toPromise()
			.then( hasValidTenant => {
				this._hasValidTenant = hasValidTenant;

				// get loggedIn users role (only once)
				if ( typeof this._isSupportAdmin !== 'boolean' ) {
					const roles: RoleTypes[] = this._currentUserService.getRoles();
					this._isSupportAdmin = roles.includes( RoleTypes.SUPPORT_ADMIN );
				}

				// logs out all users with invalid tenant, except Support Admins
				if ( !hasValidTenant && this._isSupportAdmin === false ) {
					return this.logOut();
				} else {
					return this.checkAllowedRoute( state );
				}
			} );
		}
		// prevents showing any page during logout process
		return Promise.reject( true );
	}
}
